import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Loader2, Users } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/forms/Combobox';
import { FormField, FormSelect } from '@/components/forms/FormField';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useTiers } from '@/hooks/queries/useTiers';
import { useUser } from '@/hooks/useUser';
import { useFormations, useSessions, useUpsertInscription } from '@/hooks/queries/useFormation';
import { formatDA, formatDateDZ } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { ModeReglement } from '@/lib/database.types';
import { supabase } from '@/lib/supabase';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  defaultSousSegmentIds?: string[];
  onSaved: () => void;
}

const STEPS = ['Tier + Session', 'Participants', 'Tarification', 'Récap'] as const;

interface ContactRow {
  id: string;
  nom: string;
  prenom: string | null;
  email: string | null;
}

export function InscriptionBulkDialog({
  open,
  onOpenChange,
  defaultSousSegmentIds,
  onSaved,
}: Props): JSX.Element {
  const { user } = useUser();
  const { data: entreprises = [] } = useEntreprises();
  const eurlKostGroup = entreprises.find((e) => e.nom.includes('Kost'));
  const { data: formations = [] } = useFormations({
    sousSegmentIds: defaultSousSegmentIds,
    statut: 'actif',
  });
  const formationIds = useMemo(() => formations.map((f) => f.id), [formations]);
  const { data: allSessions = [] } = useSessions({});
  const sessions = useMemo(
    () =>
      allSessions.filter(
        (s) =>
          ['ouverte_inscriptions', 'planifiee', 'complete'].includes(s.statut) &&
          (formationIds.length === 0 || formationIds.includes(s.formation_id)),
      ),
    [allSessions, formationIds],
  );
  const { data: tiers = [] } = useTiers({ entrepriseId: eurlKostGroup?.id ?? null, search: '' });
  const upsert = useUpsertInscription();

  const [step, setStep] = useState(0);
  const [tierId, setTierId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [prixUnitaire, setPrixUnitaire] = useState('');
  const [acomptePct, setAcomptePct] = useState('30');
  const [modePaiement, setModePaiement] = useState<ModeReglement>('virement');
  const [submitting, setSubmitting] = useState(false);

  const tierSelected = tiers.find((t) => t.id === tierId);
  const sessionSelected = sessions.find((s) => s.id === sessionId);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setTierId('');
    setSessionId('');
    setContacts([]);
    setSelectedContacts([]);
    setPrixUnitaire('');
    setAcomptePct('30');
    setModePaiement('virement');
  }, [open]);

  // Charger les contacts du tier sélectionné
  useEffect(() => {
    if (!tierId) {
      setContacts([]);
      return;
    }
    void supabase
      .from('tiers_contacts')
      .select('id, nom, prenom, email')
      .eq('tier_id', tierId)
      .then(({ data }) => {
        setContacts((data ?? []) as ContactRow[]);
      });
  }, [tierId]);

  // Auto-rempli prix
  useEffect(() => {
    if (sessionSelected?.prix_session && !prixUnitaire) {
      setPrixUnitaire(String(sessionSelected.prix_session));
    }
  }, [sessionSelected, prixUnitaire]);

  const tierOptions = useMemo(
    () =>
      tiers.slice(0, 500).map((tt) => ({
        value: tt.id,
        label: `${tt.code ? tt.code + ' · ' : ''}${tt.raison_sociale}`,
      })),
    [tiers],
  );

  const totalParticipants = Math.max(1, selectedContacts.length);
  const totalPrix = Number(prixUnitaire) * totalParticipants;
  const totalAcompte = (totalPrix * Number(acomptePct)) / 100;

  const handleSubmit = async (): Promise<void> => {
    if (!tierId || !sessionId || selectedContacts.length === 0 || !sessionSelected) return;
    setSubmitting(true);
    let ok = 0;
    let err = 0;
    try {
      for (const contactId of selectedContacts) {
        try {
          await upsert.mutateAsync({
            session_id: sessionId,
            tier_id: tierId,
            contact_id: contactId,
            commercial_id: user?.id ?? null,
            source: 'Bulk B2B',
            prix_facture: Number(prixUnitaire),
            acompte_pct: Number(acomptePct),
            mode_paiement: modePaiement,
            statut: 'confirme',
          });
          ok++;
        } catch {
          err++;
        }
      }
      toast.success(
        `${ok} inscription(s) créée(s)${err > 0 ? ` · ${err} échec(s) (capacité atteinte ?)` : ''}`,
      );
      onSaved();
    } finally {
      setSubmitting(false);
    }
  };

  const canNext = (): boolean => {
    if (step === 0) return !!tierId && !!sessionId;
    if (step === 1) return selectedContacts.length > 0;
    if (step === 2) return Number(prixUnitaire) > 0;
    return true;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Inscription en masse — B2B intra-entreprise</DialogTitle>
          <DialogDescription>
            Inscrivez plusieurs participants d&apos;une même entreprise cliente à une session en 4
            étapes.
          </DialogDescription>
        </DialogHeader>

        <div className="border-b border-border px-6 pb-2">
          <ol className="flex items-center gap-1 text-xs">
            {STEPS.map((s, i) => (
              <li key={s} className="flex items-center">
                <span
                  className={cn(
                    'rounded-full px-3 py-1',
                    i === step
                      ? 'bg-primary text-primary-foreground'
                      : i < step
                        ? 'bg-success/20 text-success'
                        : 'text-muted-foreground',
                  )}
                >
                  {i + 1}. {s}
                </span>
                {i < STEPS.length - 1 && (
                  <ChevronRight className="mx-1 h-3 w-3 text-muted-foreground" />
                )}
              </li>
            ))}
          </ol>
        </div>

        <DialogBody className="space-y-4">
          {step === 0 && (
            <>
              <Combobox
                label="Entreprise cliente"
                value={tierId}
                onChange={setTierId}
                options={tierOptions}
                fullWidth
                required
              />
              <FormSelect
                label="Session"
                value={sessionId}
                onChange={setSessionId}
                required
                fullWidth
                options={[
                  { value: '', label: '— Sélectionner —' },
                  ...sessions.map((s) => ({
                    value: s.id,
                    label: `${s.reference} · ${formatDateDZ(s.date_debut)} → ${formatDateDZ(s.date_fin)} · capacité ${s.capacite_max}`,
                  })),
                ]}
              />
            </>
          )}

          {step === 1 && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Sélectionnez les participants depuis les contacts de{' '}
                <strong>{tierSelected?.raison_sociale}</strong>.
              </p>
              {contacts.length === 0 ? (
                <p className="text-xs text-warning">
                  Ce tier n&apos;a aucun contact enregistré. Ajoutez des contacts dans Paramètres
                  &gt; Tiers.
                </p>
              ) : (
                <ul className="max-h-72 space-y-1 overflow-y-auto rounded-md border border-border p-2">
                  {contacts.map((c) => (
                    <li key={c.id}>
                      <label className="flex items-center gap-2 rounded p-1.5 text-xs hover:bg-muted/40">
                        <input
                          type="checkbox"
                          checked={selectedContacts.includes(c.id)}
                          onChange={(e) => {
                            setSelectedContacts((cur) =>
                              e.target.checked ? [...cur, c.id] : cur.filter((x) => x !== c.id),
                            );
                          }}
                        />
                        <span className="font-medium">
                          {c.prenom ?? ''} {c.nom}
                        </span>
                        {c.email && <span className="text-muted-foreground">— {c.email}</span>}
                      </label>
                    </li>
                  ))}
                </ul>
              )}
              <div className="text-xs text-muted-foreground">
                {selectedContacts.length} participant(s) sélectionné(s)
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <FormField
                label="Prix unitaire (DZD / participant)"
                type="number"
                step="0.01"
                min="0"
                value={prixUnitaire}
                onChange={(e) => setPrixUnitaire(e.target.value)}
                required
              />
              <FormField
                label="Acompte %"
                type="number"
                step="1"
                min="0"
                max="100"
                value={acomptePct}
                onChange={(e) => setAcomptePct(e.target.value)}
              />
              <FormSelect
                label="Mode de paiement"
                value={modePaiement}
                onChange={(v) => setModePaiement(v as ModeReglement)}
                options={[
                  { value: 'virement', label: 'Virement' },
                  { value: 'cheque', label: 'Chèque' },
                  { value: 'especes', label: 'Espèces' },
                ]}
              />
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3 rounded-md border border-border bg-muted/30 p-4 text-xs">
              <h4 className="flex items-center gap-2 text-sm font-semibold">
                <Users className="h-3.5 w-3.5" /> Récapitulatif
              </h4>
              <Row label="Entreprise cliente" value={tierSelected?.raison_sociale ?? '—'} />
              <Row
                label="Session"
                value={
                  sessionSelected
                    ? `${sessionSelected.reference} · ${formatDateDZ(sessionSelected.date_debut)} → ${formatDateDZ(sessionSelected.date_fin)}`
                    : '—'
                }
              />
              <Row label="Participants" value={`${selectedContacts.length} contact(s)`} />
              <Row label="Prix unitaire" value={formatDA(Number(prixUnitaire) || 0)} />
              <Row
                label="Total facturable"
                value={`${formatDA(totalPrix)} (acompte ${formatDA(totalAcompte)})`}
              />
              <Row label="Mode paiement" value={modePaiement} />
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Annuler
          </Button>
          {step > 0 && (
            <Button variant="outline" onClick={() => setStep(step - 1)} disabled={submitting}>
              <ChevronLeft className="h-3.5 w-3.5" /> Précédent
            </Button>
          )}
          {step < STEPS.length - 1 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!canNext()}>
              Suivant <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <Button
              onClick={() => void handleSubmit()}
              disabled={submitting || selectedContacts.length === 0}
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Créer {selectedContacts.length} inscription(s)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-[10px] text-muted-foreground">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}
